import baseUrl from "../../api/baseUrl";

export interface PosStatus {
  isOpen: boolean;
  posId?: number;
  cashIn: number; 
  cashOut: number; 
  currentBalance: number; 
  cashInTime?: string;
  openedBy?: number;
  actualCashAmount?: number;
  discrepancy?: number;
  discrepancyReason?: string;
}

export interface PosHistoryItem {
  id: number;
  cashIn: number;
  cashOut: number; 
  cashInTime: string;
  cashOutTime?: string;
  actualCashAmount?: number; 
  discrepancy?: number; 
  discrepancyReason?: string;
  reason?: string;
  createdAt: string;
  active : boolean;
}

export interface CashOperationRequest {
  amount: number;
  reason?: string;
  drawerId?: number;
}

export interface CashOperationResponse {
  success: boolean;
  message: string;
  data?: {
    newBalance?: number;
    withdrawnAmount?: number;
    finalBalance?: number;
    expectedBalance?: number;
    actualBalance?: number;
    discrepancy?: number;
    adjustmentMade?: boolean;
    adjustmentType?: string;
    adjustmentAmount?: number;
    finalCashIn?: number;
    finalCashOut?: number;
    sessionDuration?: number;
  };
}

export interface CustomerData {
  id?: number;
  name: string;
  phoneNumber: string;
  loyaltyId?: string;
  customerType: string;
  createdAt?: string;
  updatedAt?: string;
}

class PosApiService {
  // Cash In Operation
  async cashIn(data: CashOperationRequest): Promise<CashOperationResponse> {
    try {
      const response = await baseUrl.post('/branch/pos/cash-in', data);
      return response.data;
    } catch (error: any) {
      console.log('Error processing cash in:', error);
      throw new Error(error.response?.data?.message || 'Failed to process cash in');
    }
  }

  // Cash Out Operation
  async cashOut(data: CashOperationRequest): Promise<CashOperationResponse> {
    try {
      const response = await baseUrl.post('/branch/pos/cash-out', data);
      return response.data;
    } catch (error: any) {
      console.log('Error processing cash out:', error);
      throw new Error(error.response?.data?.message || 'Failed to process cash out');
    }
  }

  // Get Current POS Status
  async getPosStatus(): Promise<{ success: boolean; data: PosStatus }> {
    try {
      const response = await baseUrl.get('/branch/pos/pos-status');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get POS status');
    }
  }

  // Get POS History
  async getPosHistory(startDate?: string, endDate?: string): Promise<{ success: boolean; data: PosHistoryItem[] }> {
    try {
      let url = '/branch/pos/pos-history';
      const params = new URLSearchParams();
      
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await baseUrl.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get POS history');
    }
  }

  // Close POS Session
  async closePosSession(data?: {
    actualCashAmount: number;
    discrepancyReason?: string;
  }): Promise<CashOperationResponse> {
    try {
      const response = await baseUrl.put('/branch/pos/close-session', data);
      return response.data;
    } catch (error: any) {
      console.log('Error closing POS session:', error);
      
      throw new Error(error.response?.data?.message || 'Failed to close POS session');
    }
  }

  // Customer Management
  async getCustomers(): Promise<{ success: boolean; data: CustomerData[] }> {
    try {
      const response = await baseUrl.get('/branch/customer/get-customers');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get customers');
    }
  }

  async createCustomer(customerData: Omit<CustomerData, 'id' | 'createdAt' | 'updatedAt'>): Promise<{ success: boolean; data: CustomerData }> {
    try {
      const response = await baseUrl.post('/branch/customer/create-customer', customerData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create customer');
    }
  }

  // Legacy APIs for backward compatibility
  async createPos(cashIn: number): Promise<any> {
    try {
      const response = await baseUrl.post('/branch/pos/create-pos', { cashIn });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create POS');
    }
  }

  async updatePos(id: number, cashOut: number): Promise<any> {
    try {
      const response = await baseUrl.put(`/branch/pos/update-pos/${id}`, { cashOut });
      return response.data;
    } catch (error: any) {
      console.log('Error updating POS:', error);
      throw new Error(error.response?.data?.message || 'Failed to update POS');
    }
  }
}

export const posApiService = new PosApiService();
export default posApiService;
